import { useEffect } from 'react';
import { motion } from 'motion/react';
import { Check, Flame, ListChecks, Snowflake, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppDrawer } from '@/components/AppDrawer';
import { IconDisc } from '@/components/kit';
import { NamedIcon } from '@/lib/icons';
import { confetti } from '@/lib/confetti';
import { choreName, levelTitle, m, msg } from '@/lib/i18n';
import type { CompletionResult } from '@/lib/types';

const RARITY_COLOR: Record<string, string> = { common: 'var(--common)', rare: 'var(--rare)', epic: 'var(--epic)', legendary: 'var(--legendary)' };

/** The reward moment after finishing a chore. */
export function Celebration({ result, onClose }: { result: CompletionResult | null; onClose: () => void }) {
  useEffect(() => {
    if (result) {
      confetti();
      navigator.vibrate?.(30);
    }
  }, [result]);
  const r = result;
  const rows: { icon: React.ReactNode; title: string; sub?: string | null; style?: React.CSSProperties }[] = [];
  if (r) {
    if (r.project)
      rows.push({
        icon: <ListChecks />,
        title: r.project.finished ? m.project_finished({ name: r.project.name }) : m.project_progress({ name: r.project.name, done: r.project.done, total: r.project.total }),
        sub: r.project.finished ? m.project_bonus({ coins: r.project.bonus }) : null,
      });
    if (r.levelUp) rows.push({ icon: <TrendingUp />, title: m.level_n({ level: r.levelUp.level }), sub: m.level_up_sub({ title: levelTitle(r.levelUp) }) });
    if (r.streak > 1 && r.streak > r.streakBefore) rows.push({ icon: <Flame />, title: m.streak_days({ count: r.streak }), sub: r.usedFreezes ? m.freeze_covered() : null });
    if (r.earnedFreeze) rows.push({ icon: <Snowflake />, title: m.freeze_earned(), sub: m.freeze_earned_sub() });
    if (r.sticker)
      rows.push({
        icon: <NamedIcon name={r.sticker.icon} />,
        title: msg(`sticker_${r.sticker.id}`),
        sub: m.sticker_label({ rarity: msg(`rarity_${r.sticker.rarity}`) }),
        style: { color: RARITY_COLOR[r.sticker.rarity], background: `color-mix(in srgb, ${RARITY_COLOR[r.sticker.rarity]} 14%, transparent)` },
      });
    for (const b of r.badges) rows.push({ icon: <NamedIcon name={b.icon} />, title: msg(`badge_${b.id}_name`), sub: msg(`badge_${b.id}_desc`) });
  }
  return (
    <AppDrawer
      open={!!r}
      onOpenChange={(o) => !o && onClose()}
      title={<span className="sr-only">{m.done()}</span>}
      footer={
        <Button size="lg" onClick={onClose}>
          {m.continue()}
        </Button>
      }
    >
      {r && (
        <div className="grid justify-items-center gap-4 pt-2 text-center">
          <motion.div
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 16 }}
            className="bg-accent text-accent-foreground grid size-[72px] place-items-center rounded-full"
          >
            <Check className="size-9" strokeWidth={2.5} />
          </motion.div>
          <div>
            <motion.div initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} className="text-[40px] font-bold tracking-tight">
              +{r.xp} XP
            </motion.div>
            <p className="text-muted-foreground">
              {choreName(r.chore)}
              {r.wasOverdue && ` · ${m.rescue_bonus()}`}
            </p>
          </div>
          {rows.length > 0 && (
            <div className="divide-border w-full divide-y text-left">
              {rows.map((row, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.08 }} className="flex items-center gap-3 py-3">
                  <IconDisc tone="accent" className="[&_svg]:size-[18px]">
                    <span style={row.style} className="grid size-10 place-items-center rounded-full">
                      {row.icon}
                    </span>
                  </IconDisc>
                  <div>
                    <div className="font-medium">{row.title}</div>
                    {row.sub && <div className="text-muted-foreground text-[13px]">{row.sub}</div>}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </AppDrawer>
  );
}
